import { type ReactNode, useId } from "react";

import { LayoutGroup, type Variants, motion } from "framer-motion";
import type { LucideIcon } from "lucide-react";

import { Skeleton } from "@/components/ui/skeleton";

import { AnimatedNumber } from "@/components/shared/animated-number";

import { cn } from "@/lib/utils";

/** Ohanglar — hammasi `src/index.css` dagi tokenlardan. */
const TONES = {
  neutral: {
    icon: "bg-muted text-muted-foreground",
    text: "text-muted-foreground",
    glow: "bg-muted-foreground/15",
    bar: "bg-muted-foreground/50",
  },
  primary: {
    icon: "bg-primary/10 text-primary",
    text: "text-primary",
    glow: "bg-primary/20",
    bar: "bg-primary",
  },
  success: {
    icon: "bg-success-soft text-success",
    text: "text-success",
    glow: "bg-success/20",
    bar: "bg-success",
  },
  warning: {
    icon: "bg-warning-soft text-warning",
    text: "text-warning",
    glow: "bg-warning/25",
    bar: "bg-warning",
  },
  danger: {
    icon: "bg-destructive/10 text-destructive",
    text: "text-destructive",
    glow: "bg-destructive/20",
    bar: "bg-destructive",
  },
  info: { icon: "bg-info-soft text-info", text: "text-info", glow: "bg-info/20", bar: "bg-info" },
} as const;

export type StatTone = keyof typeof TONES;

const EASE_OUT = [0.22, 1, 0.36, 1] as const;

/** `StatGrid` ichida plitalar navbatma-navbat ko'tariladi. */
const TILE_VARIANTS: Variants = {
  hidden: { opacity: 0, y: 10 },
  show: { opacity: 1, y: 0, transition: { duration: 0.4, ease: EASE_OUT } },
};

const GRID_VARIANTS: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.06 } },
};

/**
 * Qism / jami — plita ostidagi chiziq uchun (0..1).
 *
 * Son noma'lum bo'lsa `undefined`: chiziq umuman chizilmaydi. Soxta bo'sh
 * chiziq "hech kim yo'q" deb yolg'on aytardi — xuddi soxta `0` kabi.
 */
export const shareOf = (part?: number, total?: number): number | undefined => {
  if (part === undefined || total === undefined || total <= 0) return undefined;
  return Math.min(1, Math.max(0, part / total));
};

interface StatTileProps {
  label: string;
  /** `undefined` + `isLoading` — skelet. Soxta `0` KO'RSATILMAYDI. */
  value?: number | string;
  icon?: LucideIcon;
  tone?: StatTone;
  hint?: string;
  isLoading?: boolean;
  /** So'rov yiqilgan bo'lsa raqam o'rniga chiziqcha turadi. */
  isError?: boolean;
  className?: string;
  /** 0..1 — pastdagi ulush chizig'i (masalan, faollar / jami). */
  share?: number;
  /**
   * Berilsa plita TUGMA bo'ladi: bosilganda ro'yxat shu holatga o'tadi.
   * Holat tablari o'rnida ishlaydi (ADR 0003).
   */
  onClick?: () => void;
  /** Ro'yxat hozir shu plitadagi holat bo'yicha filtrlangan. */
  active?: boolean;
  /**
   * `sm` — ixcham plita: ikonka yorliq yonida, son kichikroq, izoh bir
   * qatorda (to'liq matni `title` da). Ko'p ko'rsatkichni bitta qatorga
   * sig'dirish uchun — jadval ekrandan pastga tushib ketmasin.
   */
  size?: "default" | "sm";
}

/**
 * Bitta ko'rsatkich.
 *
 * Yuklanayotganda raqam O'RNIDA skelet turadi. Ilgari ko'p panellarda
 * `value ?? 0` yozilar va odam bir necha soniya haqiqiy nol deb o'ylardi —
 * bu esa "hech kim navbatda yo'q" degan yolg'on xabar.
 *
 * Son qiymat sanab ko'tariladi; tanlangan plitaning halqasi `layoutId`
 * orqali bir plitadan ikkinchisiga suzib o'tadi.
 */
export const StatTile = ({
  label,
  value,
  icon: Icon,
  tone = "neutral",
  hint,
  isLoading,
  isError,
  className,
  share,
  onClick,
  active,
  size = "default",
}: StatTileProps) => {
  const palette = TONES[tone];
  const known = !isLoading && !isError && value !== undefined;
  const compact = size === "sm";

  const body: ReactNode = (
    <>
      {/* Nur alohida qobiqda kesiladi: kartaning o'zi `overflow-hidden`
          bo'lsa, tanlov halqasi plitalar orasida suzganda kesilib qolardi. */}
      <span aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden rounded-xl">
        <span
          className={cn(
            "absolute rounded-full opacity-50 blur-2xl transition-opacity duration-500 group-hover:opacity-90",
            compact ? "-top-10 -right-10 size-24" : "-top-12 -right-12 size-32",
            palette.glow,
            active && "opacity-90",
          )}
        />
      </span>

      {Icon && !compact && (
        <span
          className={cn(
            "relative grid size-10 shrink-0 place-items-center rounded-xl transition-transform duration-300 group-hover:scale-105",
            palette.icon,
          )}
        >
          <Icon className="size-5" aria-hidden />
        </span>
      )}

      {/* `@container` — ixcham plitada son shrifti plita kengligiga moslashadi
          (`cqi`): tor ekranda sakkizta plita bir qatorda turganda "25-09-2026"
          kabi uzun qiymat kesilib qolmasin. */}
      <span className={cn("relative flex min-w-0 flex-1 flex-col", compact && "@container")}>
        {compact ? (
          <span className="text-muted-foreground flex min-w-0 items-center gap-1.5 text-xs">
            {Icon && <Icon className={cn("size-3.5 shrink-0", palette.text)} aria-hidden />}
            <span className="truncate" title={label}>
              {label}
            </span>
          </span>
        ) : (
          <span className="text-muted-foreground truncate text-sm">{label}</span>
        )}

        {isLoading ? (
          <Skeleton className={compact ? "mt-1 h-6 w-12" : "mt-1.5 h-7 w-16"} />
        ) : (
          <span
            className={cn(
              "font-heading mt-0.5 font-bold tracking-tight tabular-nums",
              compact
                ? "truncate text-[length:min(1.125rem,15cqi)] leading-6"
                : "text-2xl leading-8",
            )}
          >
            {!known ? "—" : typeof value === "number" ? <AnimatedNumber value={value} /> : value}
          </span>
        )}

        {hint &&
          (compact ? (
            <span className="text-muted-foreground mt-0.5 truncate text-[11px]" title={hint}>
              {hint}
            </span>
          ) : (
            <span className="text-muted-foreground mt-0.5 line-clamp-2 text-xs">{hint}</span>
          ))}

        {known && share !== undefined && (
          <span
            className={cn(
              "bg-muted block h-1 overflow-hidden rounded-full",
              compact ? "mt-1.5" : "mt-2.5",
            )}
            aria-hidden
          >
            <motion.span
              className={cn("block h-full origin-left rounded-full", palette.bar)}
              initial={{ scaleX: 0 }}
              animate={{ scaleX: share }}
              transition={{ duration: 0.9, ease: EASE_OUT, delay: 0.15 }}
            />
          </span>
        )}
      </span>

      {active && (
        <motion.span
          layoutId="stat-tile-active"
          aria-hidden
          className="border-primary pointer-events-none absolute -inset-px rounded-xl border-2"
          transition={{ type: "spring", stiffness: 420, damping: 36 }}
        />
      )}
    </>
  );

  const frame = cn(
    "group border-border bg-card relative flex items-start rounded-xl border text-start shadow-xs",
    compact ? "gap-2 px-3 py-2.5" : "gap-3 p-4",
    className,
  );

  if (onClick) {
    return (
      <motion.button
        type="button"
        variants={TILE_VARIANTS}
        whileHover={{ y: -2 }}
        whileTap={{ scale: 0.985 }}
        onClick={onClick}
        aria-pressed={active ?? false}
        className={cn(
          frame,
          "focus-visible:ring-ring/50 cursor-pointer transition-shadow outline-none hover:shadow-md focus-visible:ring-3",
          active && "shadow-md",
        )}
      >
        {body}
      </motion.button>
    );
  }

  return (
    <motion.div variants={TILE_VARIANTS} className={frame}>
      {body}
    </motion.div>
  );
};

/**
 * Plitalar qatori: navbatma-navbat paydo bo'lish va tanlov halqasining
 * o'z guruhi (`LayoutGroup`) — bir sahifadagi ikkita qator bir-biriga
 * aralashmaydi.
 */
export const StatGrid = ({ children, className }: { children: ReactNode; className?: string }) => {
  const id = useId();
  return (
    <LayoutGroup id={id}>
      <motion.section
        initial="hidden"
        animate="show"
        variants={GRID_VARIANTS}
        className={cn("grid grid-cols-2 gap-3 xl:grid-cols-4", className)}
      >
        {children}
      </motion.section>
    </LayoutGroup>
  );
};
